// Skills (FD §11) — instruction packages roles can use. Import from git or a
// local folder, then approve or block. Scripts count as executable code and
// require explicit approval before a skill can be linked to a role
// (FD §11.2) — blocking always requires a reason so that decision is
// auditable (packages/api/src/routes/skills/approve.ts).

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { RegistrySkill, Skill } from '@goblic/shared'
import { skillsApi, type SkillImportInput } from '../lib/api-client'
import {
  Drawer,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  Notice,
  PageHeader,
  Tabs,
} from '../components/ui'
import { useAuth, useWorkspaceId } from '../lib/auth'
import { useDialogs } from '../components/dialogs'
import { humanize } from '../lib/format'

export function SkillsPage() {
  const workspaceId = useWorkspaceId()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [importing, setImporting] = useState(false)
  const [importMode, setImportMode] = useState<ImportMode>('browse')

  const skillsQuery = useQuery({
    queryKey: ['skills', workspaceId],
    queryFn: () => skillsApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['skills', workspaceId] })
  }

  const importMutation = useMutation({
    mutationFn: skillsApi.import,
    onSuccess: () => {
      invalidate()
      setImporting(false)
    },
  })

  const approveMutation = useMutation({
    mutationFn: ({ skillId, note }: { skillId: string; note?: string }) =>
      skillsApi.approve(skillId, { actorId: user?.id ?? '', note }),
    onSuccess: invalidate,
  })

  const blockMutation = useMutation({
    mutationFn: ({ skillId, reason }: { skillId: string; reason: string }) =>
      skillsApi.block(skillId, { actorId: user?.id ?? '', reason }),
    onSuccess: invalidate,
  })

  const skills = skillsQuery.data?.items ?? []
  const importedNames = new Set(
    skills.map((skill) => normalizeName(skill.name))
  )

  // Stable, so the Drawer's focus/Escape effect doesn't re-run (and steal
  // focus back to the search box) every time the page re-renders.
  const resetImport = importMutation.reset
  const closeImport = useCallback(() => {
    setImporting(false)
    resetImport()
  }, [resetImport])

  return (
    <div>
      <PageHeader
        title="Skills"
        description="Reusable instruction packages that roles can load. Review a skill before agents can use it, especially if it contains scripts."
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setImporting(true)}
          >
            Import skill
          </button>
        }
      />

      <div className="panel overflow-x-auto">
        {skillsQuery.isLoading ? (
          <LoadingRows />
        ) : skillsQuery.isError ? (
          <ErrorNote error={skillsQuery.error} what="skills" />
        ) : skills.length === 0 ? (
          <EmptyState
            title="No skills yet"
            action={
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setImporting(true)}
              >
                Import a skill
              </button>
            }
          >
            A skill is a folder with a SKILL.md and a skill.yaml. Import one
            from a git repository or a folder on the server.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Skill</th>
                <th>Source</th>
                <th className="w-px" />
              </tr>
            </thead>
            <tbody>
              {skills.map((skill) => (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  isSubmitting={
                    approveMutation.isPending || blockMutation.isPending
                  }
                  onApprove={() =>
                    approveMutation.mutate({ skillId: skill.id })
                  }
                  onBlock={(reason) =>
                    blockMutation.mutate({ skillId: skill.id, reason })
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={importing}
        onClose={closeImport}
        title="Import a skill"
        description="Goblic reads the manifest and shows the skill here for review."
        width="max-w-2xl"
      >
        <div className="space-y-5">
          <Tabs
            label="Import source"
            value={importMode}
            onChange={(mode) => {
              setImportMode(mode)
              importMutation.reset()
            }}
            options={[
              { value: 'browse', label: 'Browse skills.sh' },
              { value: 'git', label: 'Git repository' },
              { value: 'local', label: 'Folder on server' },
            ]}
          />
          {importMode === 'browse' ? (
            <RegistryBrowser
              workspaceId={workspaceId}
              importedNames={skillsQuery.isSuccess ? importedNames : null}
              onImported={invalidate}
            />
          ) : (
            <ImportForm
              key={importMode}
              sourceKind={importMode}
              workspaceId={workspaceId}
              isSubmitting={importMutation.isPending}
              error={
                importMutation.isError
                  ? (importMutation.error as Error).message
                  : null
              }
              onCancel={closeImport}
              onSubmit={(input) => importMutation.mutate(input)}
            />
          )}
        </div>
      </Drawer>
    </div>
  )
}

function SkillRow({
  skill,
  isSubmitting,
  onApprove,
  onBlock,
}: {
  skill: Skill
  isSubmitting: boolean
  onApprove: () => void
  onBlock: (reason: string) => void
}) {
  const { confirm, prompt } = useDialogs()

  async function approve(): Promise<void> {
    const ok = await confirm({
      kind: 'question',
      title: `Approve ${skill.name}?`,
      message:
        'Roles can load it once it’s approved. Any scripts it contains run inside agent sandboxes.',
      confirmLabel: 'Approve skill',
    })
    if (ok) onApprove()
  }

  async function block(): Promise<void> {
    const reason = await prompt({
      kind: 'warning',
      title: `Block ${skill.name}?`,
      message:
        'Roles can’t load a blocked skill. The reason goes into the audit log.',
      label: 'Why block it?',
      placeholder: 'Runs unreviewed shell scripts',
      confirmLabel: 'Block skill',
    })
    if (reason) onBlock(reason)
  }

  return (
    <tr>
      <td>
        <span className="block font-medium text-ink">{skill.name}</span>
        <span className="mono-id" title={skill.currentVersionId}>
          version {skill.currentVersionId.slice(0, 8)}
        </span>
      </td>
      <td>
        <span className="block">{humanize(skill.sourceKind)}</span>
        <span className="block max-w-xs truncate font-mono text-xs text-ink-3">
          {skill.sourceRef}
        </span>
      </td>
      <td>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={isSubmitting}
            onClick={() => void approve()}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn-ghost btn-sm text-danger"
            disabled={isSubmitting}
            onClick={() => void block()}
          >
            Block
          </button>
        </div>
      </td>
    </tr>
  )
}

function ImportForm({
  sourceKind,
  workspaceId,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: {
  sourceKind: 'local' | 'git'
  workspaceId: string
  isSubmitting: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (input: SkillImportInput) => void
}) {
  const [path, setPath] = useState('')
  const [url, setUrl] = useState('')
  const [ref, setRef] = useState('')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (sourceKind === 'local') onSubmit({ source: 'local', workspaceId, path })
    else onSubmit({ source: 'git', workspaceId, url, ref: ref || undefined })
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      {sourceKind === 'git' ? (
        <>
          <Field label="Repository URL" htmlFor="skill-url">
            <input
              id="skill-url"
              className="field-input"
              placeholder="https://github.com/org/skill-repo.git"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          </Field>
          <Field
            label="Branch, tag or commit"
            htmlFor="skill-ref"
            hint="Optional. Defaults to the repository’s default branch."
          >
            <input
              id="skill-ref"
              className="field-input"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </Field>
        </>
      ) : (
        <Field
          label="Folder path"
          htmlFor="skill-path"
          hint="An absolute path on the machine running the Goblic API."
        >
          <input
            id="skill-path"
            className="field-input font-mono"
            placeholder="/srv/skills/my-skill"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            required
          />
        </Field>
      )}

      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Importing…' : 'Import skill'}
        </button>
      </div>
    </form>
  )
}

type ImportMode = 'browse' | 'git' | 'local'

/** Skill names are compared loosely: "My Skill" and "my-skill" match. */
function normalizeName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

const COMPACT = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

/** 752300 → "752.3k", 1200000 → "1.2m". */
function formatInstalls(installs: number): string {
  return COMPACT.format(installs).toLowerCase()
}

/**
 * The import route replies with the raw `{ skill, version }` import result
 * (typed as Skill on the client), whose skill carries `activationState`.
 * Read it defensively so either shape works.
 */
function isPendingApproval(result: unknown): boolean {
  const record = result as {
    activationState?: string
    skill?: { activationState?: string }
  } | null
  const state = record?.skill?.activationState ?? record?.activationState
  return state === 'pending_approval'
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

/**
 * Search the public skills.sh registry and import a skill in one click.
 * Stays open after an import so several skills can be picked in a row.
 */
function RegistryBrowser({
  workspaceId,
  importedNames,
  onImported,
}: {
  workspaceId: string
  /** Names already in this workspace, or null while the list is unknown. */
  importedNames: Set<string> | null
  onImported: () => void
}) {
  const [search, setSearch] = useState('')
  const query = useDebounced(search.trim(), 300)
  const isSearch = query.length >= 2
  // Rows imported (or failed) during this session, keyed by registry id.
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [done, setDone] = useState<Map<string, { needsApproval: boolean }>>(
    new Map()
  )
  const [errors, setErrors] = useState<Map<string, string>>(new Map())

  const popularQuery = useQuery({
    queryKey: ['skills-registry', 'popular'],
    queryFn: () => skillsApi.registryPopular(),
    enabled: !isSearch,
    staleTime: 5 * 60_000,
  })

  const searchQuery = useQuery({
    queryKey: ['skills-registry', 'search', query],
    queryFn: () => skillsApi.registrySearch(query, 30),
    enabled: isSearch,
    staleTime: 60_000,
  })

  const active = isSearch ? searchQuery : popularQuery
  const results = active.data?.skills ?? []
  const isTyping = search.trim() !== query

  const importMutation = useMutation({
    mutationFn: (entry: RegistrySkill) =>
      skillsApi.import({ source: 'registry', workspaceId, id: entry.id }),
    onMutate: (entry) => {
      setPending((prev) => new Set(prev).add(entry.id))
      setErrors((prev) => {
        const next = new Map(prev)
        next.delete(entry.id)
        return next
      })
    },
    onSuccess: (result, entry) => {
      setDone((prev) =>
        new Map(prev).set(entry.id, {
          needsApproval: isPendingApproval(result),
        })
      )
      onImported()
    },
    onError: (error, entry) => {
      setErrors((prev) => new Map(prev).set(entry.id, (error as Error).message))
    },
    onSettled: (_data, _error, entry) => {
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(entry.id)
        return next
      })
    },
  })

  return (
    <div className="space-y-4">
      <Field
        label="Search skills.sh"
        htmlFor="registry-search"
        hint="Skills that include scripts need approval before roles can use them."
      >
        <input
          id="registry-search"
          type="search"
          className="field-input"
          placeholder="e.g. frontend design, pdf, testing"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Field>

      <div>
        <h3 className="mb-2 text-sm font-medium text-ink-3">
          {isSearch ? `Results for “${query}”` : 'Popular skills'}
        </h3>
        <div className="panel overflow-hidden">
          {active.isLoading || (isTyping && !active.data) ? (
            <LoadingRows label={isSearch ? 'Searching…' : 'Loading…'} />
          ) : active.isError ? (
            <ErrorNote
              error={active.error}
              what={isSearch ? 'search results' : 'popular skills'}
            />
          ) : results.length === 0 ? (
            <p className="px-5 py-8 text-center text-base text-ink-3">
              {isSearch
                ? 'No skills match that search.'
                : 'No popular skills to show right now.'}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {results.map((entry) => (
                <RegistryRow
                  key={entry.id}
                  entry={entry}
                  isPending={pending.has(entry.id)}
                  imported={done.get(entry.id) ?? null}
                  alreadyImported={
                    importedNames?.has(normalizeName(entry.name)) ?? false
                  }
                  error={errors.get(entry.id) ?? null}
                  onImport={() => importMutation.mutate(entry)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

function RegistryRow({
  entry,
  isPending,
  imported,
  alreadyImported,
  error,
  onImport,
}: {
  entry: RegistrySkill
  isPending: boolean
  /** Set once this row was imported in the current session. */
  imported: { needsApproval: boolean } | null
  alreadyImported: boolean
  error: string | null
  onImport: () => void
}) {
  const isImported = imported != null || alreadyImported
  const needsApproval = imported?.needsApproval ?? false

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <a
            href={entry.url}
            target="_blank"
            rel="noreferrer"
            className="truncate font-medium text-ink hover:underline"
            title="Open on skills.sh"
          >
            {entry.name}
          </a>
          <span
            className="text-sm text-ink-3"
            title={`${entry.installs.toLocaleString()} installs`}
          >
            {formatInstalls(entry.installs)} installs
          </span>
        </div>
        <span className="block truncate font-mono text-xs text-ink-3">
          {entry.source}
        </span>
        {needsApproval ? (
          <span className="mt-1 block text-sm text-signal">
            Imported. It contains scripts, so approve it before use.
          </span>
        ) : null}
        {error ? (
          <span role="alert" className="mt-1 block text-sm text-danger">
            {error}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <a
          href={entry.url}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost btn-sm"
        >
          View
        </a>
        <button
          type="button"
          className="btn-secondary btn-sm min-w-[5.5rem]"
          disabled={isPending || isImported}
          onClick={onImport}
        >
          {isPending ? 'Importing…' : isImported ? 'Imported' : 'Import'}
        </button>
      </div>
    </li>
  )
}
